/**
 * 全局站点设置 Context
 * 在应用顶层获取设置并下发给所有组件
 */

import React, { createContext, useContext, useEffect } from 'react';
import { useSettings } from '../hooks/use-settings';
import type { SiteSettings, FeaturesConfig } from '../api/types';

interface SettingsContextValue {
  site: SiteSettings;
  features: FeaturesConfig;
  isLoading: boolean;
}

const DEFAULT_SITE: SiteSettings = {
  title: '弗瑞云游',
  subtitle: '让免费更自由',
  description: '弗瑞云游 - 免费云游戏平台',
  keywords: '云游戏,免费游戏,在线游戏',
  logo: '',
  favicon: '',
  icp: '',
};

const DEFAULT_FEATURES: FeaturesConfig = {
  search: true,
  tags: true,
  categories: true,
  darkMode: true,
  comments: false,
  analytics: false,
};

const SettingsContext = createContext<SettingsContextValue>({
  site: DEFAULT_SITE,
  features: DEFAULT_FEATURES,
  isLoading: true,
});

export const useSiteSettings = () => useContext(SettingsContext);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { site, features, isLoading } = useSettings();

  // 动态更新页面 title、favicon 和 meta 标签
  useEffect(() => {
    if (site) {
      // 更新页面标题
      document.title = site.subtitle ? `${site.title} - ${site.subtitle}` : site.title;

      // 更新 favicon
      if (site.favicon) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = site.favicon;
      }

      // 更新 meta description
      if (site.description) {
        let meta = document.querySelector("meta[name='description']") as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement('meta');
          meta.name = 'description';
          document.head.appendChild(meta);
        }
        meta.content = site.description;
      }

      // 更新 meta keywords
      if (site.keywords) {
        let meta = document.querySelector("meta[name='keywords']") as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement('meta');
          meta.name = 'keywords';
          document.head.appendChild(meta);
        }
        meta.content = site.keywords;
      }
    }
  }, [site]);

  return (
    <SettingsContext.Provider
      value={{
        site: site || DEFAULT_SITE,
        features: features || DEFAULT_FEATURES,
        isLoading,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
